import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertTriangle, Camera, Send, Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const concernTypes = [
  { value: 'taste', label: 'Taste Issue', emoji: '👅' },
  { value: 'smell', label: 'Unusual Smell', emoji: '👃' },
  { value: 'appearance', label: 'Appearance', emoji: '👀' },
  { value: 'temperature', label: 'Temperature', emoji: '🌡️' },
  { value: 'foreign_object', label: 'Foreign Object', emoji: '⚠️' },
  { value: 'illness_symptoms', label: 'Illness', emoji: '🤒' }
];

export default function AppFoodSafety() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    order_id: '',
    meal_name: '',
    consumption_date: '',
    consumption_time: '',
    concern_type: '',
    description: '',
    has_symptoms: false,
    symptoms: '',
    symptom_onset_time: '',
    image_url: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, WEBP allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB allowed');
      return;
    }

    // Show preview immediately
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_URL}/api/chat/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: uploadFormData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      handleChange('image_url', `${API_URL}${data.image_url}`);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.message);
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    handleChange('image_url', '');
  };

  const handleSubmit = async () => {
    if (!formData.meal_name || !formData.consumption_date || !formData.concern_type || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.has_symptoms && !formData.symptoms) {
      toast.error('Please describe your symptoms');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/food-safety`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          order_id: formData.order_id || null,
          meal_name: formData.meal_name,
          consumption_date: formData.consumption_date,
          consumption_time: formData.consumption_time || '12:00',
          concern_type: formData.concern_type,
          description: formData.description,
          has_symptoms: formData.has_symptoms,
          symptoms: formData.has_symptoms ? formData.symptoms : null,
          symptom_onset_time: formData.has_symptoms ? formData.symptom_onset_time : null,
          image_url: formData.image_url || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit report');
      }

      toast.success('Report submitted successfully');
      navigate('/messages');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.meal_name || !formData.consumption_date)) {
      toast.error('Please fill in meal details');
      return;
    }
    if (step === 2 && !formData.concern_type) {
      toast.error('Please select a concern type');
      return;
    }
    setStep(prev => prev + 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background"
      data-testid="app-food-safety"
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-red-600">
        <div className="px-5 py-4 flex items-center gap-4">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </motion.button>
          <div>
            <h1 className="font-heading text-xl font-semibold text-white">Food Safety Report</h1>
            <p className="text-white/80 text-xs">Step {step} of 4</p>
          </div>
        </div>
        {/* Progress */}
        <div className="h-1 bg-white/20">
          <motion.div 
            className="h-full bg-white"
            initial={{ width: '25%' }}
            animate={{ width: `${step * 25}%` }}
          />
        </div>
      </div>

      <div className="px-5 py-6 pb-32">
        {/* Step 1: Meal Details */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-secondary-900">What meal was affected?</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-900 mb-2 block">Meal Name *</label>
                <input
                  type="text"
                  value={formData.meal_name}
                  onChange={(e) => handleChange('meal_name', e.target.value)}
                  placeholder="e.g., Butter Chicken"
                  className="w-full h-14 px-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20"
                  data-testid="meal-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-secondary-900 mb-2 block">Order ID (if available)</label>
                <input
                  type="text"
                  value={formData.order_id}
                  onChange={(e) => handleChange('order_id', e.target.value)}
                  placeholder="e.g., order_abc123"
                  className="w-full h-14 px-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-secondary-900 mb-2 block">Date Consumed *</label>
                <input
                  type="date"
                  value={formData.consumption_date}
                  onChange={(e) => handleChange('consumption_date', e.target.value)}
                  className="w-full h-14 px-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20"
                  data-testid="consumption-date"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Concern Type */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-semibold text-secondary-900">What type of concern?</h2>
              <p className="text-muted-foreground text-sm">Select the most relevant option</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {concernTypes.map((type) => (
                <motion.button
                  key={type.value}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleChange('concern_type', type.value)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    formData.concern_type === type.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-border bg-white'
                  }`}
                  data-testid={`concern-${type.value}`}
                >
                  <span className="text-2xl mb-2 block">{type.emoji}</span>
                  <span className="font-medium text-secondary-900 text-sm">{type.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 3: Description */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-semibold text-secondary-900">Describe the issue</h2>
              <p className="text-muted-foreground text-sm">Be as detailed as possible</p>
            </div>

            <div className="space-y-4">
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Please describe what you experienced..."
                rows={5}
                className="w-full p-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20 resize-none"
                data-testid="description"
              />

              {/* Image Upload */}
              <div>
                <label className="text-sm font-medium text-secondary-900 mb-2 block">Upload Photos (optional)</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                
                {!imagePreview && !formData.image_url ? (
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full h-32 border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center gap-2 text-muted-foreground hover:bg-muted/50 transition-colors"
                    data-testid="upload-image-btn"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : (
                      <>
                        <Camera className="h-8 w-8" />
                        <span className="text-sm">Tap to upload photo</span>
                        <span className="text-xs">Max 5MB • JPEG, PNG, GIF, WEBP</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <div className="relative">
                    <img 
                      src={imagePreview || formData.image_url} 
                      alt="Uploaded" 
                      className="w-full h-48 object-cover rounded-2xl"
                    />
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                        <Loader2 className="h-8 w-8 text-white animate-spin" />
                      </div>
                    )}
                    <button
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-2 bg-black/60 rounded-full text-white hover:bg-black/80"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Symptoms */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl font-semibold text-secondary-900">Any symptoms?</h2>
            </div>

            <div className="space-y-4">
              <div className="flex gap-3">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleChange('has_symptoms', true)}
                  className={`flex-1 p-4 rounded-2xl border-2 ${
                    formData.has_symptoms ? 'border-red-500 bg-red-50' : 'border-border bg-white'
                  }`}
                >
                  <span className="font-medium text-secondary-900">Yes</span>
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleChange('has_symptoms', false)}
                  className={`flex-1 p-4 rounded-2xl border-2 ${
                    !formData.has_symptoms ? 'border-green-500 bg-green-50' : 'border-border bg-white'
                  }`}
                >
                  <span className="font-medium text-secondary-900">No</span>
                </motion.button>
              </div>

              {formData.has_symptoms && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <textarea
                    value={formData.symptoms}
                    onChange={(e) => handleChange('symptoms', e.target.value)}
                    placeholder="Describe your symptoms..."
                    rows={3}
                    className="w-full p-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20 resize-none"
                    data-testid="symptoms"
                  />
                  <input
                    type="text"
                    value={formData.symptom_onset_time}
                    onChange={(e) => handleChange('symptom_onset_time', e.target.value)}
                    placeholder="When did symptoms start? (e.g., 2 hours after)"
                    className="w-full h-14 px-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-red-500/20"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Bottom Action */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-5 safe-area-bottom">
        {step < 4 ? (
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={nextStep}
            className="w-full h-14 bg-red-600 text-white rounded-2xl font-semibold"
          >
            Continue
          </motion.button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-14 bg-red-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="submit-report"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Send className="h-5 w-5" />
                Submit Report
              </>
            )}
          </motion.button>
        )}
        
        {step > 1 && (
          <button
            onClick={() => setStep(prev => prev - 1)}
            className="w-full text-center text-muted-foreground mt-3"
          >
            Go Back
          </button>
        )}
      </div>
    </motion.div>
  );
}
